import { useMemo } from 'react';
import { CombinedProperty } from '../types';
import { CA_COUNTIES_CONFIG } from '../../../mocks/caForeclosures';
import { NV_COUNTIES_CONFIG } from '../../../mocks/nvForeclosures';
import { AZ_COUNTIES_CONFIG } from '../../../mocks/azForeclosures';

type StateFilter = 'All' | 'CA' | 'NV' | 'AZ';

interface Filters {
  minTax: number;
  maxTax: number;
  propertyType: string;
  state: StateFilter;
  status: string;
  counties: Set<string>;
}

interface CombinedSidebarProps {
  filters: Filters;
  onFiltersChange: (f: Filters) => void;
  filteredProperties: CombinedProperty[];
  allProperties: CombinedProperty[];
  onFocusState: (state: string) => void;
}

const PROPERTY_TYPES = ['All Types', 'Single Family', 'Multi Family', 'Commercial', 'Vacant Land', 'Industrial', 'Condo'];
const STATUS_OPTIONS = ['All Status', 'Active', 'Pending', 'Upcoming', 'Sold'];
const STATE_OPTIONS: StateFilter[] = ['All', 'CA', 'NV', 'AZ'];

const STATE_COLORS: Record<string, string> = {
  CA: '#f97316',
  NV: '#f59e0b',
  AZ: '#22c55e',
};

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

const fmtShort = (n: number) => {
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
  return `$${n}`;
};

export const CombinedSidebar: React.FC<CombinedSidebarProps> = ({
  filters,
  onFiltersChange,
  filteredProperties,
  allProperties,
  onFocusState,
}) => {
  const stats = useMemo(() => {
    const count = filteredProperties.length;
    const totalValue = filteredProperties.reduce((s, p) => s + p.assessedValue, 0);
    const avgTaxes = count > 0 ? filteredProperties.reduce((s, p) => s + p.taxesOwed, 0) / count : 0;
    return { count, totalValue, avgTaxes };
  }, [filteredProperties]);

  const stateStats = useMemo(() => {
    const result: Record<string, { count: number; totalValue: number }> = { CA: { count: 0, totalValue: 0 }, NV: { count: 0, totalValue: 0 }, AZ: { count: 0, totalValue: 0 } };
    filteredProperties.forEach(p => {
      if (result[p.state]) {
        result[p.state].count++;
        result[p.state].totalValue += p.assessedValue;
      }
    });
    return result;
  }, [filteredProperties]);

  const visibleCounties = useMemo(() => {
    if (filters.state === 'All') return null;
    const configs = filters.state === 'CA' ? CA_COUNTIES_CONFIG : filters.state === 'NV' ? NV_COUNTIES_CONFIG : AZ_COUNTIES_CONFIG;
    return Object.keys(configs);
  }, [filters.state]);

  const getCountyConfig = (county: string, state: string) => {
    if (state === 'CA') return CA_COUNTIES_CONFIG[county];
    if (state === 'NV') return NV_COUNTIES_CONFIG[county];
    return AZ_COUNTIES_CONFIG[county];
  };

  const toggleCounty = (countyKey: string) => {
    const next = new Set(filters.counties);
    if (next.has(countyKey)) { next.delete(countyKey); } else { next.add(countyKey); }
    onFiltersChange({ ...filters, counties: next });
  };

  const allCountyKeys = useMemo(() => {
    const keys: string[] = [];
    Object.keys(CA_COUNTIES_CONFIG).forEach(c => keys.push(`CA:${c}`));
    Object.keys(NV_COUNTIES_CONFIG).forEach(c => keys.push(`NV:${c}`));
    Object.keys(AZ_COUNTIES_CONFIG).forEach(c => keys.push(`AZ:${c}`));
    return keys;
  }, []);

  const resetFilters = () => {
    onFiltersChange({
      minTax: 0,
      maxTax: 50000,
      propertyType: 'All Types',
      state: 'All',
      status: 'All Status',
      counties: new Set(allCountyKeys),
    });
  };

  return (
    <aside className="w-80 flex-shrink-0 bg-[#111827] border-r border-gray-700/50 flex flex-col overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-700/50">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-filter-3-line text-amber-400 text-base"></i>
          </div>
          <h2 className="text-white font-semibold text-sm tracking-wide uppercase">Map Filters</h2>
        </div>
        <p className="text-gray-400 text-xs">Multi-State Tax Foreclosure Properties</p>
      </div>

      {/* Global Stats */}
      <div className="px-5 py-4 border-b border-gray-700/50 grid grid-cols-3 gap-2">
        <div className="bg-[#1f2937] rounded-lg p-3 text-center">
          <div className="text-amber-400 font-bold text-lg leading-none">{stats.count}</div>
          <div className="text-gray-400 text-xs mt-1">Properties</div>
        </div>
        <div className="bg-[#1f2937] rounded-lg p-3 text-center">
          <div className="text-emerald-400 font-bold text-sm leading-none">{fmtShort(stats.totalValue)}</div>
          <div className="text-gray-400 text-xs mt-1">Total Value</div>
        </div>
        <div className="bg-[#1f2937] rounded-lg p-3 text-center">
          <div className="text-rose-400 font-bold text-sm leading-none">{fmt(stats.avgTaxes)}</div>
          <div className="text-gray-400 text-xs mt-1">Avg Taxes</div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        {/* State Toggle */}
        <div>
          <label className="block text-gray-300 text-xs font-semibold uppercase tracking-wide mb-2">State</label>
          <div className="grid grid-cols-4 gap-1.5">
            {STATE_OPTIONS.map(s => (
              <button
                key={s}
                onClick={() => {
                  onFiltersChange({ ...filters, state: s });
                  if (s !== 'All') onFocusState(s);
                  else onFocusState('All');
                }}
                className={`text-xs py-1.5 px-2 rounded-md border transition-colors cursor-pointer whitespace-nowrap font-semibold ${
                  filters.state === s
                    ? s === 'All'
                      ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                      : `border-opacity-50 text-white`
                    : 'border-gray-600 text-gray-400 hover:border-gray-500'
                }`}
                style={filters.state === s && s !== 'All' ? {
                  backgroundColor: `${STATE_COLORS[s]}22`,
                  borderColor: `${STATE_COLORS[s]}80`,
                  color: STATE_COLORS[s],
                } : {}}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Per-State Stats */}
        <div>
          <label className="block text-gray-300 text-xs font-semibold uppercase tracking-wide mb-2">By State</label>
          <div className="space-y-2">
            {(['CA', 'NV', 'AZ'] as const).map(st => (
              <div
                key={st}
                className="flex items-center gap-3 bg-[#1f2937] rounded-lg px-3 py-2.5 cursor-pointer hover:bg-[#263040] transition-colors"
                onClick={() => { onFiltersChange({ ...filters, state: st }); onFocusState(st); }}
              >
                <div
                  className="w-8 h-8 flex items-center justify-center rounded-md flex-shrink-0 font-bold text-xs"
                  style={{ backgroundColor: `${STATE_COLORS[st]}22`, color: STATE_COLORS[st], border: `1px solid ${STATE_COLORS[st]}44` }}
                >
                  {st}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-gray-200 text-sm font-medium">
                    {st === 'CA' ? 'California' : st === 'NV' ? 'Nevada' : 'Arizona'}
                  </div>
                  <div className="text-gray-500 text-xs">{fmtShort(stateStats[st].totalValue)} total value</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-bold text-sm" style={{ color: STATE_COLORS[st] }}>{stateStats[st].count}</div>
                  <div className="text-gray-500 text-xs">props</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Taxes Owed Range */}
        <div>
          <label className="block text-gray-300 text-xs font-semibold uppercase tracking-wide mb-3">Taxes Owed Range</label>
          <div className="space-y-3">
            <div className="flex justify-between text-xs text-gray-400">
              <span>{fmt(filters.minTax)}</span>
              <span>{fmt(filters.maxTax)}</span>
            </div>
            <div className="space-y-2">
              <div>
                <label className="text-gray-500 text-xs mb-1 block">Min: {fmt(filters.minTax)}</label>
                <input
                  type="range" min={0} max={50000} step={500} value={filters.minTax}
                  onChange={e => { const v = Number(e.target.value); if (v < filters.maxTax) onFiltersChange({ ...filters, minTax: v }); }}
                  className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                  style={{ accentColor: '#f59e0b' }}
                />
              </div>
              <div>
                <label className="text-gray-500 text-xs mb-1 block">Max: {fmt(filters.maxTax)}</label>
                <input
                  type="range" min={0} max={50000} step={500} value={filters.maxTax}
                  onChange={e => { const v = Number(e.target.value); if (v > filters.minTax) onFiltersChange({ ...filters, maxTax: v }); }}
                  className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                  style={{ accentColor: '#f59e0b' }}
                />
              </div>
            </div>
            <div className="flex gap-2">
              {[10000, 25000, 50000].map(preset => (
                <button
                  key={preset}
                  onClick={() => onFiltersChange({ ...filters, minTax: 0, maxTax: preset })}
                  className={`flex-1 text-xs py-1 rounded-md border transition-colors cursor-pointer whitespace-nowrap ${
                    filters.maxTax === preset && filters.minTax === 0
                      ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                      : 'border-gray-600 text-gray-400 hover:border-gray-500'
                  }`}
                >
                  &lt;{fmt(preset / 1000)}k
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Property Type */}
        <div>
          <label className="block text-gray-300 text-xs font-semibold uppercase tracking-wide mb-2">Property Type</label>
          <select
            value={filters.propertyType}
            onChange={e => onFiltersChange({ ...filters, propertyType: e.target.value })}
            className="w-full bg-[#1f2937] border border-gray-600 text-gray-200 text-sm rounded-lg px-3 py-2.5 cursor-pointer focus:outline-none focus:border-amber-500/60"
          >
            {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {/* Status */}
        <div>
          <label className="block text-gray-300 text-xs font-semibold uppercase tracking-wide mb-2">Auction Status</label>
          <div className="grid grid-cols-2 gap-1.5">
            {STATUS_OPTIONS.map(s => (
              <button
                key={s}
                onClick={() => onFiltersChange({ ...filters, status: s })}
                className={`text-xs py-1.5 px-2 rounded-md border transition-colors cursor-pointer whitespace-nowrap ${
                  filters.status === s
                    ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                    : 'border-gray-600 text-gray-400 hover:border-gray-500'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* County List (when state is selected) */}
        {filters.state !== 'All' && visibleCounties && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-gray-300 text-xs font-semibold uppercase tracking-wide">
                {filters.state} Counties
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const configs = filters.state === 'CA' ? CA_COUNTIES_CONFIG : filters.state === 'NV' ? NV_COUNTIES_CONFIG : AZ_COUNTIES_CONFIG;
                    const next = new Set(filters.counties);
                    Object.keys(configs).forEach(c => next.add(`${filters.state}:${c}`));
                    onFiltersChange({ ...filters, counties: next });
                  }}
                  className="text-xs text-amber-400 hover:text-amber-300 cursor-pointer whitespace-nowrap"
                >All</button>
                <span className="text-gray-600">|</span>
                <button
                  onClick={() => {
                    const configs = filters.state === 'CA' ? CA_COUNTIES_CONFIG : filters.state === 'NV' ? NV_COUNTIES_CONFIG : AZ_COUNTIES_CONFIG;
                    const next = new Set(filters.counties);
                    Object.keys(configs).forEach(c => next.delete(`${filters.state}:${c}`));
                    onFiltersChange({ ...filters, counties: next });
                  }}
                  className="text-xs text-gray-400 hover:text-gray-300 cursor-pointer whitespace-nowrap"
                >None</button>
              </div>
            </div>
            <div className="space-y-1">
              {visibleCounties.map(county => {
                const countyKey = `${filters.state}:${county}`;
                const config = getCountyConfig(county, filters.state);
                const checked = filters.counties.has(countyKey);
                const count = filteredProperties.filter(p => p.county === county && p.state === filters.state).length;
                if (!config) return null;
                return (
                  <div key={countyKey} className="flex items-center gap-2.5 py-2 px-2.5 rounded-lg hover:bg-[#1f2937] transition-colors">
                    <button
                      onClick={() => toggleCounty(countyKey)}
                      className="w-4 h-4 flex-shrink-0 rounded border-2 flex items-center justify-center transition-all cursor-pointer"
                      style={{ borderColor: config.color, backgroundColor: checked ? config.color : 'transparent' }}
                    >
                      {checked && <i className="ri-check-line text-white text-xs leading-none"></i>}
                    </button>
                    <div className="flex-1 flex items-center justify-between">
                      <span className={`text-sm ${checked ? 'text-gray-200' : 'text-gray-500'}`}>{county}</span>
                      <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full"
                        style={{ backgroundColor: checked ? `${config.color}22` : 'transparent', color: checked ? config.color : '#6b7280' }}
                      >{count}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Reset */}
        <button
          onClick={resetFilters}
          className="w-full py-2.5 border border-gray-600 text-gray-400 text-sm rounded-lg hover:border-amber-500/50 hover:text-amber-400 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line mr-2"></i>Reset All Filters
        </button>
      </div>
    </aside>
  );
};

export default CombinedSidebar;
